import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import type {
  CustomerReport,
  ExpiringDocumentsReport,
  Granularity,
  OutstandingReport,
  RevenueReport,
  VisitsReport
} from './types';

export function useRevenueReport(groupBy: Granularity, from?: string, to?: string) {
  return useQuery({
    queryKey: ['report', 'revenue', { groupBy, from, to }],
    queryFn: async () => {
      const res = await apiClient.get<RevenueReport>('/api/v1/reports/revenue', {
        params: { groupBy, from, to }
      });
      return res.data;
    },
    staleTime: 30_000
  });
}

export function useOutstandingReport(olderThanDays: number) {
  return useQuery({
    queryKey: ['report', 'outstanding', { olderThanDays }],
    queryFn: async () => {
      const res = await apiClient.get<OutstandingReport>('/api/v1/reports/outstanding', {
        params: { olderThanDays }
      });
      return res.data;
    },
    staleTime: 30_000
  });
}

export function useCustomerReport(customerId: string | undefined, from?: string, to?: string) {
  return useQuery({
    queryKey: ['report', 'customer', { customerId, from, to }],
    queryFn: async () => {
      const res = await apiClient.get<CustomerReport>(`/api/v1/reports/customer/${customerId}`, {
        params: { from, to }
      });
      return res.data;
    },
    enabled: !!customerId,
    staleTime: 30_000
  });
}

/** Fetch the customer-history PDF as a blob and trigger a browser download. */
export async function downloadCustomerHistoryPdf(customerId: string, from?: string, to?: string) {
  const res = await apiClient.get<Blob>(`/api/v1/reports/customer/${customerId}/pdf`, {
    params: { from, to },
    responseType: 'blob'
  });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = parseFilename(res.headers['content-disposition']) ?? 'customer-history.pdf';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Render the same PDF server-side and email it to the signed-in user. */
export async function emailCustomerHistory(customerId: string, from?: string, to?: string) {
  await apiClient.post(`/api/v1/reports/customer/${customerId}/email`, { from, to });
}

function parseFilename(contentDisposition?: string): string | null {
  if (!contentDisposition) return null;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(contentDisposition);
  return match ? decodeURIComponent(match[1]) : null;
}

export function useVisitsReport(from?: string, to?: string) {
  return useQuery({
    queryKey: ['report', 'visits', { from, to }],
    queryFn: async () => {
      const res = await apiClient.get<VisitsReport>('/api/v1/reports/visits', {
        params: { from, to }
      });
      return res.data;
    },
    staleTime: 30_000
  });
}

export function useExpiringDocumentsReport(withinDays: number) {
  return useQuery({
    queryKey: ['report', 'expiring', { withinDays }],
    queryFn: async () => {
      const res = await apiClient.get<ExpiringDocumentsReport>('/api/v1/reports/documents-expiring', {
        params: { withinDays }
      });
      return res.data;
    },
    staleTime: 30_000
  });
}
